import React, { useState, useEffect, useRef } from 'react';
import '../styles/CustomSelect.css';

const CustomSelect = ({
  className,
  value,
  defaultValue,
  onChange,
  disabled = false,
  id,
  name,
  children,
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    !isControlled ? (defaultValue !== undefined ? String(defaultValue) : '') : undefined
  );
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [dropLeft, setDropLeft] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const currentValue = isControlled ? String(value ?? '') : (internalValue ?? '');

  const options = [];
  React.Children.forEach(children, child => {
    if (!child || child.type !== 'option') return;
    options.push({
      value: child.props.value !== undefined ? String(child.props.value) : '',
      label: child.props.children,
      disabled: !!child.props.disabled,
      hidden: !!child.props.hidden,
    });
  });

  const visibleOptions = options.filter(opt => !opt.disabled && !opt.hidden);
  const selectedOption = options.find(opt => opt.value === currentValue);
  const displayText = selectedOption ? selectedOption.label : (options[0]?.label ?? '');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      if (rect.right > window.innerWidth - 4) {
        setDropLeft(true);
      }
    }
    if (!isOpen) {
      setDropLeft(false);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropUp(window.innerHeight - rect.bottom < 220 && rect.top > window.innerHeight - rect.bottom);
      }
      setIsOpen(prev => !prev);
    }
  };

  const handleSelect = (optValue) => {
    if (!isControlled) setInternalValue(optValue);
    setIsOpen(false);
    onChange?.({ target: { name, value: optValue } });
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(prev => !prev);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) { setIsOpen(true); return; }
      const idx = visibleOptions.findIndex(opt => opt.value === currentValue);
      const next = e.key === 'ArrowDown'
        ? Math.min(idx + 1, visibleOptions.length - 1)
        : Math.max(idx - 1, 0);
      if (visibleOptions[next]) handleSelect(visibleOptions[next].value);
    }
  };

  const triggerClass = `custom-select-trigger${className ? ` ${className}` : ' cs-default'}`;

  return (
    <div
      className={`custom-select-container${isOpen ? ' cs-open' : ''}${disabled ? ' cs-disabled' : ''}${dropUp ? ' cs-dropup' : ''}${dropLeft ? ' cs-drop-left' : ''}`}
      ref={containerRef}
    >
      <button
        type="button"
        className={triggerClass}
        id={id}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="custom-select-value">{displayText}</span>
        <svg
          className="custom-select-arrow"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div className="custom-select-dropdown" role="listbox" ref={dropdownRef}>
          {visibleOptions.map((opt, i) => (
            <div
              key={`${opt.value}-${i}`}
              className={`custom-select-option${opt.value === currentValue ? ' cs-selected' : ''}`}
              role="option"
              aria-selected={opt.value === currentValue}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt.value);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
